import React, { useEffect, useRef } from 'react';
import CommonComponent from "../../CommonComponent";
import usePersistentDateRange from '../../Common/usePersistentDateRange';
import useInteractiveSalesMap from './useInteractiveSalesMap';
import stateCoordinates from './state.json';

import * as am4core from "@amcharts/amcharts4/core";
import * as am4maps from "@amcharts/amcharts4/maps";
import am4geodata_india from "@amcharts/amcharts4-geodata/indiaLow";
import am4themes_animated from "@amcharts/amcharts4/themes/animated";
import DashboardReady from '../DashboardReady/DashboardReady'
import { useOption } from '../../../../context/OptionContext';
import PermissionControlledCard from '../../../../common/PermissionControlledCard';
import { usePermissions } from '../../../../context/PermissionContext';
import AllowedCard from '../../../../common/AllowedCard';

am4core.useTheme(am4themes_animated);

// Convert state names to Title Case
const toTitleCase = (str) =>
  str.replace(/\w\S*/g, (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase());

const InteractiveSalesMap = () => {
  const chartRef = useRef(null);
  
  const {
    interactiveSalesMapState,
    getInteractiveSalesMap,
    accessDenied
  } = useInteractiveSalesMap();
  const {selectedOption} = useOption();
  
  const {
    selectedRange,
    handleDateChange,
    formattedStart,
    formattedEnd,
  } = usePersistentDateRange();
  
  useEffect(() => {
    getInteractiveSalesMap({ formattedEnd, formattedStart });
  }, [formattedStart, formattedEnd,selectedOption?.id]);
  
  const permission = usePermissions();
  // Prepare map data with safe fallback
const mapViewPermissions = permission?.data?.interactive_sales_map?.map_view || {};

const mapData = (interactiveSalesMapState || []).map((item) => {
  const tooltipLines = [];

  const addLine = (key, label, value) => {
    const p = mapViewPermissions?.[key];
    if (!p) return;

    // If both display and allowed true → show label + value
    if (p.display && p.allowed) {
      tooltipLines.push(`${label}: ${value ?? 0}`);
    }
    // If not allowed → show only icon (no label)
    else if (p.display || p.allowed) {
      // tooltipLines.push(``);
    }
  };

  addLine("net_sales", "Net Sales", item?.net_sales);
  addLine("net_income", "Net Income", item?.net_income);
  addLine("gross_unit", "Gross Units", item?.gross_unit);
  addLine("cancelled_unit", "Cancelled Units", item?.cancelled_units);
  addLine("replacement_unit", "Replacement Units", item?.replacement_units);
  addLine("refunded_unit", "Refunded Units", item?.refunded_units);
  addLine("actual_sold_unit", "Actual Sold Units", item?.actual_sold_units);

  // If all metrics hidden → no tooltip
  const allFalse = Object.values(mapViewPermissions).every(
    (v) => !v.display || !v.allowed
  );

  const tooltipContent = !allFalse
    ? `${item?.state}\n${tooltipLines.join("\n")}`
    : "";

  return {
    ...item,
    state: toTitleCase(item?.state),
    value: item?.actual_sold_units || 0,
    tooltip: tooltipContent,
  };
});


  // Get top 5 states by units sold
  const topStates = [...mapData]
    .sort((a, b) => b.actual_sold_units - a.actual_sold_units)
    .slice(0, 5);

  useEffect(() => {
    const chart = am4core.create("chartdiv", am4maps.MapChart);
    chartRef.current = chart;

    chart.geodata = am4geodata_india;
    chart.projection = new am4maps.projections.Miller();
    chart.homeGeoPoint = { latitude: 23, longitude: 83 };

    const polygonSeries = chart.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    polygonSeries.exclude = ["AQ"];
    polygonSeries.data = mapData;

    const polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.tooltipText = "";
    polygonTemplate.fill = am4core.color("#e0e0e0");
    polygonTemplate.propertyFields.tooltipText = "tooltip";
    polygonTemplate.propertyFields.fill = "fill";

    polygonSeries.heatRules.push({
      property: "fill",
      target: polygonTemplate,
      min: am4core.color("#bde2ff"),
      max: am4core.color("#0366d6"),
    });

    // Add labels to states
    const labelSeries = chart.series.push(new am4maps.MapImageSeries());
    labelSeries.data = mapData.map((state) => ({
      ...state,
      ...stateCoordinates[state.id] || {},
    }));

    const imageTemplate = labelSeries.mapImages.template;
    imageTemplate.propertyFields.latitude = "latitude";
    imageTemplate.propertyFields.longitude = "longitude";
    imageTemplate.nonScaling = true;

    const label = imageTemplate.createChild(am4core.Label);
    label.fill = am4core.color(
      window.matchMedia("(prefers-color-scheme: dark)").matches ? "#fff" : "#000"
    );
    label.fontSize = 13;
    label.horizontalCenter = "middle";
    label.verticalCenter = "middle";
    label.dy = -10;
    label.truncate = true;
    label.wrap = true;
    label.maxWidth = 80;

    const bullet = imageTemplate.createChild(am4core.Circle);
    bullet.radius = 4;
    bullet.fill = am4core.color("#a100ff");
    bullet.stroke = am4core.color("#fff");
    bullet.strokeWidth = 2;

    return () => chart.dispose();
  }, [interactiveSalesMapState]);
  const dashboardReady = localStorage.getItem('dashboard-ready') === 'true';
  return (
    <>
      <CommonComponent
        defaultRange={selectedRange}
        onRangeChange={handleDateChange}
      />
      <PermissionControlledCard permission={permission?.data?.interactive_sales_map?.map_view?.actual_sold_unit} colClass=''>
      {!dashboardReady ?  <DashboardReady /> :
      <section className="section dashboard py-2">
        <div style={{ position: 'relative' }}>
          <div className="interactive-map">
            <strong style={{ display: 'block', marginBottom: 8 }}>Top 5 States (Units Sold)</strong>
            <ol style={{ paddingLeft: 16, margin: 0 }}>
              {topStates.filter(state => state?.state && state?.state !== "undefined" && state?.state !== null).map((state) => (
                <li key={state?.id} style={{ marginBottom: 4 }}>
                  <strong>{state?.state}</strong> :- {state?.actual_sold_units}
                </li>
              ))}
            </ol>
          </div>
          <div id="chartdiv" style={{ width: "100%", height: "830px" }}></div>
        </div>
      </section>}
      </PermissionControlledCard>
      {accessDenied && (
      <div
        className="d-flex justify-content-center"
        style={{ textAlign: "center", padding: "30px", fontWeight: "500" }}
      >
        <AllowedCard />
      </div>
    )}
    </>
  );
};

export default InteractiveSalesMap;
